import './TableOfContents.scss';

import { CellValueChangedEvent, ColDef, GridOptions } from '@amzn/gft-ag-grid-enterprise';
import { AurigaGrid, AurigaGridRef } from '@components/Common/AurigaGrid/AurigaGrid';
import AurigaLoader from '@components/Common/AurigaLoader';
import PageSequenceCellRenderer from '@components/Common/CellRenderers/PageSequenceCellRenderer';
import HeaderControls from '@components/Common/HeaderControls/HeaderControls';
import { NotificationType } from '@components/Common/Notification';
import { globalGridDefaults } from '@constants/agGrid';
import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useTOCContext } from '@context/TOCContext';
import AuditHistoryDialog from '@deckManagement/components/AuditHistory/AuditHistoryDialog';
import AsyncSaveButton from '@deckManagement/components/Common/AsyncSaveButton/AsyncSaveButton';
import LockDeckButton from '@deckManagement/components/Common/LockDeckButton/LockDeckButton';
import ActionsCellRenderer from '@deckManagement/components/TOC/CellRenderers/ActionsCellRenderer';
import EditableCellRenderer from '@deckManagement/components/TOC/CellRenderers/EditableCellRenderer';
import PageNameCellRenderer from '@deckManagement/components/TOC/CellRenderers/PageNameCellRenderer';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { createId } from '@paralleldrive/cuid2';
import { ScheduleType } from '@services/api/deckManagement/graphql/schema/API';
import { format } from 'date-fns';
import { forEach, isEmpty } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageSequenceCellRendererProps } from 'types/Common.types';
import { CellValueChangeParams, EditableCellParams } from 'types/TOC.types';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

/**
 * TableOfContents Component
 * Displays a table of contents for a deck with actions for each page
 */
const TableOfContents = () => {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { deckLockStatus } = useDeckContext();
  const {
    tocEntries,
    currentView,
    deletePage,
    moveToActiveWithScheduleType,
    discardChanges,
    saveChanges,
    scheduleType,
    setScheduleType,
    togglePageVisibility,
    // updateTocEntry, // General updater based on field and value
    updateTocPageId,
    isLoading,
    isGetDeckColumnForDeckLoading,
    hasInitialDataLoaded,
    hasUnsavedChanges,
    updateTocEntry,
    isSaving,
  } = useTOCContext();
  const { toastNotification, featureFlags } = useAurigaContext();
  const gridRef = useRef<AurigaGridRef<EnhancedTOCEntry>>(null); // Grid ref for API access
  useRecordLoadComplete(!isLoading && hasInitialDataLoaded);

  const [simultaneousEditingCells, setSimultaneousEditingCells] = useState<Set<string>>(new Set());
  const [gridKey, setGridKey] = useState(0); // Force grid re-render when needed
  const [isRowDragging, setIsRowDragging] = useState<boolean>(false);
  const [gridReady, setGridReady] = useState<boolean>(false);

  /**
   * Handle toggle page visibility with centralized highlighting logic
   * @param {string} tocId - The TOC ID of the page to toggle visibility
   */
  const handleTogglePageVisibility = useCallback(
    (tocId: string) => {
      togglePageVisibility(tocId);
    },
    [togglePageVisibility],
  );

  /**
   * Handle moving page to active with highlighting
   * @param {string} tocId - The TOC ID of the page being moved to active
   * @param {ScheduleType} scheduleType - The schedule type for the active page
   */
  const handleMoveToActiveWithScheduleType = useCallback(
    (tocId: string, targetScheduleType: ScheduleType) => {
      moveToActiveWithScheduleType(tocId, targetScheduleType);
    },
    [moveToActiveWithScheduleType],
  );

  /**
   * Handle notification from child components
   */
  const handleNotification = useCallback(
    (message: string, type: NotificationType) => {
      toastNotification({
        visible: true,
        message,
        type,
      });
    },
    [toastNotification],
  );

  /**
   * Handles data validation and cleanup with TOC Items or currentView state changes
   */
  useEffect(() => {
    /**
     * NOTE: Duplicate Page IDs seem a valid use case from ingested data
     * Due to this we rely instead on toc_id for uniqueness from Table of Contents entries
     * Can remove this debugging output before release to Prod
     */
    const pageIds = tocEntries.map((entry) => entry.page_id);
    const duplicateIds = pageIds.filter((id, index) => pageIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      console.debug('Duplicate page_ids found:', duplicateIds);
    }
    // Check for null/undefined page_ids
    const nullIds = tocEntries.filter((entry) => !entry.page_id);
    if (nullIds.length > 0) {
      console.debug('Entries with null/undefined page_id:', nullIds);
    }

    // If nullIds exist, update those entries with a formated page_id based on data, so that our AG-Grid works correctly
    if (nullIds.length > 0) {
      forEach(nullIds, (nullIdEntry) => {
        const newPageId = createId();

        updateTocPageId(nullIdEntry.pk!, nullIdEntry.sk!, newPageId);
      });
    }
    // TODO: Remove this added debug output on release to production
    if (!isEmpty(duplicateIds) || !isEmpty(nullIds)) console.debug('TOC Entries', tocEntries);
  }, [currentView, tocEntries, updateTocPageId]);

  /**
   * Handle page deletion and show notification
   * @param {string} tocId - The TOC ID of the page to delete
   * @param {string} tocPageName - The name of the page being deleted, so we can surface it to the User
   */
  const handleDeletePage = useCallback(
    (tocId: string, tocPageName: string) => {
      deletePage(tocId);

      // Show notification
      toastNotification({
        visible: true,
        message: `Page: ${tocPageName} has been staged for deletion`,
        type: 'info',
      });
    },
    [deletePage, toastNotification],
  );

  /**
   * Start simultaneous editing for both toc_page_name and page_name fields
   */
  const handleStartSimultaneousEdit = useCallback((tocId: string) => {
    const cellKeys = [`${tocId}-toc_page_name`, `${tocId}-page_name`];
    setSimultaneousEditingCells(new Set(cellKeys));
  }, []);

  /**
   * Start simultaneous editing for rename operations
   * Uses the context's renamePage function and handles the UI state
   */
  const handleStartRename = useCallback((tocId: string) => {
    // Set up simultaneous editing for both fields
    const cellKeys = [`${tocId}-toc_page_name`, `${tocId}-page_name`];
    setSimultaneousEditingCells(new Set(cellKeys));
  }, []);

  /**
   * Check if a specific cell is in simultaneous editing mode
   */
  const isInSimultaneousEditMode = useCallback(
    (tocId: string, field: 'toc_page_name' | 'page_name') => {
      return simultaneousEditingCells.has(`${tocId}-${field}`);
    },
    [simultaneousEditingCells],
  );

  /**
   * Handle creating a new page
   * Navigates to the Page Setup Wizard for guided page creation
   */
  const handleCreateNewPage = useCallback(() => {
    navigate(`/deck-preparation/decks/${deckId}/page-setup/new`);
  }, [navigate, deckId]);

  /**
   * Handler to end simultaneous editing for a specific cell
   */
  const handleEndSimultaneousEdit = useCallback((tocId: string, field: 'toc_page_name' | 'page_name') => {
    const cellKey = `${tocId}-${field}`;
    setSimultaneousEditingCells((prev) => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
  }, []);

  /**
   * Handles the cell value change event
   * Calls the appropriate update function based on the field being edited
   * @type {(params: CellValueChangeParams) => void}
   */
  const handleCellValueChange: (params: CellValueChangeParams) => void = useCallback(
    (params: CellValueChangeParams) => {
      const { data, field, newValue } = params;
      if (!data?.toc_id || !newValue) return;

      const nameFields: Array<keyof EnhancedTOCEntry> = ['toc_page_name', 'page_name'];

      // Update TOCContext with the new value
      if (nameFields.includes(field)) {
        updateTocEntry(data.metadata.internalId, field, newValue);
      }

      // Handle UI state
      if (isInSimultaneousEditMode(data.toc_id, field as 'toc_page_name' | 'page_name')) {
        handleEndSimultaneousEdit(data.toc_id, field as 'toc_page_name' | 'page_name');
      }
    },
    [isInSimultaneousEditMode, updateTocEntry, handleEndSimultaneousEdit],
  );

  /**
   * Adapter function to convert AgGrid's CellValueChangedEvent to CellValueChangeParams
   */
  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent) => {
      const { data, colDef, newValue } = event;
      handleCellValueChange({
        data,
        field: colDef.field! as keyof EnhancedTOCEntry,
        newValue,
      });
    },
    [handleCellValueChange],
  );

  /**
   * Updates the AGGrid Row Data without redrawing the whole component
   *
   * Applies custom grid sorting logic, to consider the unique case of navigating between 3 view states:
   * 1) inactive, 2) active-approved, 3) active-modified. This allows repositioning of an item across views in a way
   * that the item will appear at the end of existing Grid items. Clicking Save persists this change.
   */
  useEffect(() => {
    if (gridRef?.current?.api && tocEntries && gridReady) {
      console.debug('reloading TOC Entries');
      gridRef.current.api.setGridOption('rowData', tocEntries);

      gridRef.current.api.applyColumnState({
        state: [
          { colId: 'viewWithScheduleType', sort: 'asc' },
          { colId: 'serial_no', sort: 'asc' },
        ],
        defaultState: { sort: null },
      });
    }
  }, [tocEntries, scheduleType, currentView, gridReady]);

  const columnDefs: ColDef[] = [
    {
      field: 'metadata.sortPos',
      headerName: 'Serial No', // Visually indicating this sortPos column as representing Serial No to the User in the FE
      initialFlex: 2,
      rowDrag: true,
      cellRenderer: PageSequenceCellRenderer,
      cellRendererParams: {
        isRowDragging,
        readonly: false,
      } as PageSequenceCellRendererProps,
      editable: false,
      hide: true,
    },
    {
      field: 'serial_no',
      headerName: 'Serial Number',
      initialFlex: 2,
      editable: false,
      hide: true,
    },
    {
      // Adds a custom hidden column to make it easier to sort and position items based off current view, and schedule type
      colId: 'viewWithScheduleType',
      headerName: 'ViewScheduleType',
      valueGetter: () => {
        if (currentView === 'inactive') return 'inactive';
        return scheduleType === ScheduleType.APPROVED ? 'active-approved' : 'active-modified';
      },
      sortable: true,
      hide: true,
    },
    {
      field: 'page_name',
      headerName: 'Page Name',
      flex: 4,
      cellRenderer: PageNameCellRenderer,
      cellRendererParams: {
        isEditing: (params: EditableCellParams) => isInSimultaneousEditMode(params.data.toc_id!, 'page_name'),
        onEndEdit: (params: EditableCellParams) => {
          handleEndSimultaneousEdit(params.data.toc_id!, 'page_name');
        },
        onCellValueChange: handleCellValueChange,
        deckId,
      },
      editable: false,
      suppressKeyboardEvent: () => true, // Allows arrow keypresses to work inside the cellRenderer
    },
    {
      field: 'toc_page_name',
      headerName: 'TOC Name',
      initialFlex: 4,
      cellRenderer: EditableCellRenderer,
      cellRendererParams: {
        isEditing: (params: EditableCellParams) => isInSimultaneousEditMode(params.data.toc_id!, 'toc_page_name'),
        onEndEdit: (params: EditableCellParams) => {
          handleEndSimultaneousEdit(params.data.toc_id!, 'toc_page_name');
        },
        onCellValueChange: handleCellValueChange,
      },
      editable: false,
      suppressKeyboardEvent: () => true, // Allows arrow keypresses to work inside the cellRenderer
    },
    {
      field: 'last_updated_date',
      headerName: 'Last Updated',
      flex: 4,
      valueFormatter: (params) => {
        // Format date (in local timezone) if exists
        return params.value ? format(params.value, 'MM/dd/yyyy hh:mm aa') : '-';
      },
    },
    {
      headerName: '',
      field: 'actions',
      flex: 2,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: {
        onToggleVisibility: handleTogglePageVisibility,
        onStartRename: handleStartRename,
        onStartSimultaneousEdit: handleStartSimultaneousEdit,
        onDelete: handleDeletePage,
        onNotification: handleNotification,
        onMoveToActive: handleMoveToActiveWithScheduleType,
        currentView,
        isEditingDisabled: deckLockStatus.isEditingDisabled,
      },
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: false,
  };

  /**
   * AGGrid Grid Options
   */
  const gridOptions: GridOptions<EnhancedTOCEntry> = useMemo(() => {
    return {
      rowClassRules: {
        'modified-row': (params) => (params.data?.metadata.isModified || params.data?.metadata.isNewRow) ?? false,
        'hidden-row': (params) => params.data?.is_visible === false,
      },
      getRowId: (params) => params.data.metadata.internalId,
    };
  }, []);

  /**
   * Handles discarding changes
   * Calls the context's discard changes function and clears the editing state
   * @type {() => void}
   */
  const handleDiscardChanges: () => void = useCallback(() => {
    setSimultaneousEditingCells(new Set()); // Clear simultaneous editing state
    discardChanges(); // Call context's discard changes
  }, [discardChanges]);

  /**
   * Handle saving changes
   * Syncs grid data to TOCContext before saving
   */
  const handleSaveChanges: () => void = useCallback(() => {
    saveChanges(() => {
      // Clear all editing states after successful save
      setSimultaneousEditingCells(new Set());
      // Force grid to completely re-render to clear highlighting
      setGridKey((prev) => prev + 1);

      toastNotification({
        visible: true,
        message: 'Page Library changes saved successfully.',
        type: 'success',
      });
    });
  }, [saveChanges, toastNotification]);

  /**
   * Renders the content based on the loading, error, and data state
   * Displays a loading message, error message, or the table of contents component.
   * @returns {ReactNode}
   */
  const renderContent = (): ReactNode => {
    if (isLoading || !hasInitialDataLoaded) {
      return <AurigaLoader loaderText='Loading Page Library...' />;
    }

    return (
      <Box className='grid-container row-highlighting'>
        {hasInitialDataLoaded && (
          <AurigaGrid<EnhancedTOCEntry>
            key={`ag-grid-${gridKey}`}
            {...globalGridDefaults}
            onGridReady={() => setGridReady(true)}
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            suppressColumnMoveAnimation
            getRowId={(params) => params.data?.metadata?.internalId}
            rowDragManaged
            animateRows
            suppressMaintainUnsortedOrder
            suppressAnimationFrame
            onCellValueChanged={onCellValueChanged}
            onRowDragEnter={() => setIsRowDragging(true)}
            onRowDragEnd={() => setIsRowDragging(false)}
          />
        )}
      </Box>
    );
  };

  return (
    <Box className='table-of-contents'>
      <Box className='header'>
        <Box className='header-title-section'>
          <Typography variant='h5'>{currentView === 'active' ? 'Page Library' : 'Inactive Pages'}</Typography>
          {currentView === 'active' && (
            <Box className='header-controls-gap'>
              <HeaderControls
                selects={[
                  {
                    label: 'Type',
                    value: scheduleType,
                    options: [
                      { value: ScheduleType.APPROVED.toString(), label: 'Approved schedule' },
                      { value: ScheduleType.MODIFIED.toString(), label: 'Modified schedule' },
                    ],
                    onChange: (value: string) => setScheduleType(value as ScheduleType),
                  },
                ]}
              />
            </Box>
          )}
          {isGetDeckColumnForDeckLoading && (
            <Box className='toc-deck-columns-loader'>
              <Typography variant='body1'>Gathering additional Page data...</Typography>
              <LinearProgress />
            </Box>
          )}
        </Box>
        <Box className='button-group'>
          {featureFlags?.enableAuditHistory && (
            <Button
              className='audit-history-button'
              variant='outlined'
              size='medium'
              startIcon={<AccessTimeIcon />}
              onClick={() => setSearchParams({ history: 'deck' })}
            >
              Deck History
            </Button>
          )}
          {currentView === 'inactive' && (
            <Button
              variant='contained'
              color='primary'
              startIcon={<AddIcon />}
              onClick={handleCreateNewPage}
              disabled={isSaving || deckLockStatus.isEditingDisabled}
              className='action-button'
            >
              New Page
            </Button>
          )}
          {hasUnsavedChanges() && (
            <>
              <Button variant='outlined' color='secondary' className='action-button' onClick={handleDiscardChanges}>
                Discard
              </Button>
              <AsyncSaveButton
                inProgress={isSaving}
                inProgressContent='Please Wait...'
                buttonProps={{
                  className: 'action-button',
                  variant: 'contained',
                  color: 'primary',
                  disabled: isSaving || deckLockStatus.isEditingDisabled,
                  onClick: handleSaveChanges,
                }}
              >
                <Typography variant='inherit'>Save Changes</Typography>
              </AsyncSaveButton>
            </>
          )}
          {/* Lock/Release Deck Button */}
          <Box className='lock-deck-section'>
            <LockDeckButton />
          </Box>
        </Box>
      </Box>
      {renderContent()}
      {/* Audit History Dialog (Deck) */}
      {featureFlags?.enableAuditHistory && <AuditHistoryDialog />}
    </Box>
  );
};

export default TableOfContents;
